import React, { useState, useEffect, useCallback } from "react";
import { API_URL } from "../config";

interface AdminBooking {
  id: number;
  created_at: string;
  start_at: string;
  end_at: string;
  status: string;
  stripe_payment_intent_id: string | null;
  grand_total_cents: number;
  host_payout_cents: number | null;
  host_fee_cents: number | null;
  service_fee_cents: number | null;
  authorized_at: string | null;
  captured_at: string | null;
  sauna_name: string;
  host_id: number;
  host_email: string | null;
  host_phone: string | null;
  host_name: string | null;
  guest_id: number;
  guest_email: string | null;
  guest_phone: string | null;
  guest_name: string | null;
}

const TOKEN_KEY = "admin_jwt";

function money(cents: number | null): string {
  if (cents == null) return "—";
  return `$${(cents / 100).toFixed(2)}`;
}

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    year: "numeric", month: "short", day: "numeric", hour: "numeric", minute: "2-digit",
  });
}

export default function AdminBookings() {
  const [token, setToken] = useState<string | null>(() => sessionStorage.getItem(TOKEN_KEY));
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState<string | null>(null);
  const [loggingIn, setLoggingIn] = useState(false);

  const [bookings, setBookings] = useState<AdminBooking[] | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async (authToken: string) => {
    setLoading(true);
    setLoadError(null);
    try {
      const res = await fetch(`${API_URL}/api/admin/bookings`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      if (res.status === 401 || res.status === 403) {
        sessionStorage.removeItem(TOKEN_KEY);
        setToken(null);
        setLoadError("Session expired — please log in again.");
        return;
      }
      const data = await res.json();
      if (!res.ok) {
        setLoadError(data.error ?? "Failed to load bookings");
        return;
      }
      setBookings(data.bookings);
    } catch {
      setLoadError("Connection failed — please try again");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (token) load(token);
  }, [token, load]);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoginError(null);
    setLoggingIn(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (!res.ok) {
        setLoginError(data.error ?? "Login failed");
        return;
      }
      sessionStorage.setItem(TOKEN_KEY, data.token);
      setToken(data.token);
      setPassword("");
    } catch {
      setLoginError("Connection failed — please try again");
    } finally {
      setLoggingIn(false);
    }
  };

  if (!token) {
    return (
      <div style={{ maxWidth: 360, margin: "80px auto", padding: "0 20px" }}>
        <h1 style={{ fontSize: 20, marginBottom: 20 }}>Admin</h1>
        <form onSubmit={handleLogin}>
          {loginError && <p style={{ color: "#ef4444", fontSize: 14, marginBottom: 12 }}>{loginError}</p>}
          <input
            type="password"
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            autoFocus
            style={{ width: "100%", padding: 10, boxSizing: "border-box", fontSize: 16, marginBottom: 12 }}
          />
          <button type="submit" disabled={loggingIn || !password} style={{ width: "100%", padding: "10px 20px" }}>
            {loggingIn ? "Checking…" : "Enter"}
          </button>
        </form>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 1400, margin: "0 auto", padding: "24px 20px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
        <h1 style={{ fontSize: 20, margin: 0 }}>Accepted Bookings</h1>
        <button
          onClick={() => { sessionStorage.removeItem(TOKEN_KEY); setToken(null); setBookings(null); }}
          style={{ padding: "6px 14px", fontSize: 13, cursor: "pointer" }}
        >
          Log out
        </button>
      </div>

      {loading && <p style={{ color: "#9ca3af" }}>Loading…</p>}
      {loadError && <p style={{ color: "#ef4444" }}>{loadError}</p>}

      {bookings && (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ textAlign: "left", borderBottom: "2px solid #e5e7eb" }}>
                <th style={th}>Booked</th>
                <th style={th}>Sauna</th>
                <th style={th}>Date</th>
                <th style={th}>Host</th>
                <th style={th}>Host phone</th>
                <th style={th}>Host email</th>
                <th style={th}>Guest</th>
                <th style={th}>Guest phone</th>
                <th style={th}>Guest email</th>
                <th style={th}>Status</th>
                <th style={th}>Guest paid</th>
                <th style={th}>Platform fee</th>
                <th style={th}>Host payout</th>
                <th style={th}>Payment intent</th>
              </tr>
            </thead>
            <tbody>
              {bookings.map(b => {
                const platformFeeCents =
                  (b.service_fee_cents ?? 0) + (b.host_fee_cents ?? 0);
                return (
                  <tr key={b.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                    <td style={td}>{fmtDate(b.created_at)}</td>
                    <td style={td}>{b.sauna_name}</td>
                    <td style={td}>{fmtDate(b.start_at)}</td>
                    <td style={td}>
                      {b.host_name || `#${b.host_id}`}
                    </td>
                    <td style={td}>{b.host_phone ?? "—"}</td>
                    <td style={td}>{b.host_email ?? "—"}</td>
                    <td style={td}>
                      {b.guest_name || `#${b.guest_id}`}
                    </td>
                    <td style={td}>{b.guest_phone ?? "—"}</td>
                    <td style={td}>{b.guest_email ?? "—"}</td>
                    <td style={td}>{b.status}</td>
                    <td style={td}>{money(b.grand_total_cents)}</td>
                    <td style={td}>{money(platformFeeCents)}</td>
                    <td style={{ ...td, fontWeight: 600 }}>{money(b.host_payout_cents)}</td>
                    <td style={{ ...td, fontFamily: "monospace", fontSize: 11, color: "#6b7280" }}>
                      {b.stripe_payment_intent_id ?? "—"}
                    </td>
                  </tr>
                );
              })}
              {bookings.length === 0 && (
                <tr><td style={td} colSpan={14}>No accepted bookings yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const th: React.CSSProperties = { padding: "8px 10px", whiteSpace: "nowrap", color: "#6b7280", fontWeight: 600 };
const td: React.CSSProperties = { padding: "8px 10px", whiteSpace: "nowrap" };
